import React, { useState, useEffect, forwardRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
    DndContext,
    closestCenter,
    MouseSensor,
    TouchSensor,
    DragOverlay,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    useSortable,
    arrayMove,
    SortableContext,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    get as _get,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Chip from '@mui/material/Chip';

import ArrowUp from '@mui/icons-material/ArrowDropUp';
import ArrowDown from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Cancel';

import Validator, { useValidate, checkValidity } from '../../../components/validator';

import Auth, { User } from '../../../data/auth-service';
import ReportsService from '../../../data/reports-service';
import UsersService from '../../../data/users-service';
import DataContext from '../../../data/data-context';

const useStyles = makeStyles(theme => ({
    reportColumn: {
        display: 'inline',
        background: theme.palette.secondary.main,
        padding: theme.spacing(2),
        marginRight: theme.spacing(1),
        borderRadius: '3px',
    },
    iconButton: {
        padding: '3px',
    },
    buttonRight: {
        float: 'right',
        marginRight: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    sortBackground: {
        background: theme.palette.secondary.main,
    },
    select: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        width: '100%',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: 36,
    },
    chip: {
        margin: 2,
        background: 'transparent',
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        fontSize: 16
    },
}));

const columns = [
    { name: 'Sev', value: 'severity', selected: true, sort: 'asc' },
    { name: 'Sensor', value: 'name', selected: true, sort: 'desc' },
    { name: 'Src IP', value: 'src_ip', selected: true, sort: '' },
    { name: 'Src Port', value: 'src_port', selected: true, sort: '' },
    { name: 'Dest IP', value: 'dest_ip', selected: true, sort: '' },
    { name: 'Dest Port', value: 'dest_port', selected: true, sort: '' },
    { name: 'Message', value: 'signature', selected: true, sort: '' },
    { name: 'Sig ID', value: 'signature_id', selected: true, sort: '' },
    { name: 'Timestamp', value: 'timestamp', selected: true, sort: '' },
    { name: 'Host', value: 'host', selected: false, sort: '' },
    { name: 'Category', value: 'category', selected: false, sort: '' },
    { name: 'Compressed', value: 'compressed', selected: false, sort: '' }
];

const Column = forwardRef(({ id, ...props }, ref) => {
    const theme = useTheme();
    const {
        onRemove,
        onSortAsc,
        onSortDesc,
        ...rest
    } = props;

    const handleDelete = () => {
        props.onRemove(props.name);
    };

    const handleSortUp = () => {
        props.onSortAsc();
    };

    const handleSortDown = () => {
        props.onSortDesc();
    };

    return (
        <div {...rest} ref={ref}>
            <Chip
                style={{ margin: '5px 5px' }}
                color="secondary"
                label={props.name}
                icon={<Box>
                    <ArrowUp
                        onMouseDown={(e) => { e.preventDefault() }}
                        onClick={handleSortUp}
                        style={{
                            marginTop: '5px',
                            borderRadius: '50px',
                            background: (props.sort === 'asc' ? 'white' : 'none'),
                            color: (props.sort === 'asc' ? theme.palette.secondary.main : 'white'),
                        }}
                    />
                    <ArrowDown
                        onMouseDown={(e) => { e.preventDefault() }}
                        onClick={handleSortDown}
                        style={{
                            marginTop: '5px',
                            borderRadius: '50px',
                            background: (props.sort === 'desc' ? 'white' : 'none'),
                            color: (props.sort === 'desc' ? theme.palette.secondary.main : 'white'),
                        }}
                    />
                </Box>}
                deleteIcon={<DeleteIcon onMouseDown={(e) => { e.preventDefault() }} />}
                onDelete={handleDelete}
            />
        </div>
    );
});

Column.propTypes = {
    id: PropTypes.string,
    sort: PropTypes.string,
    name: PropTypes.string.isRequired,
    onRemove: PropTypes.func.isRequired,
    onSortAsc: PropTypes.func,
    onSortDesc: PropTypes.func,
};

const SortableColumn = (props) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: props.name });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Column
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            {...props} />
    );
};

SortableColumn.propTypes = {
    name: PropTypes.string.isRequired,
};

const DEFAULT_PARAMETERS = {
    days: 14,
    limit: 1000,
    output_cols: ['severity', 'name', 'src_ip', 'src_port', 'dest_ip', 'dest_port', 'signature', 'signature_id'],
    severity: { 1: true, 2: true, 3: false, 4: false },
    sort_cols: [['severity', 'asc'], ['name', 'desc']],
};

const CreateReport = (props) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const [isUpdate, setIsUpdate] = useState(false);
    const [reportUUID, setReportUUID] = useState('');
    const [reportConstants, setReportConstants] = useState({});
    const user = User();
    const [report, setReport] = useState({
        additional_recipients: [],
        attachments: {
            csv: true,
            html: false,
            rst: false
        },
        body_text: false,
        csv_header: false,
        frequency: '@daily',
        frequency_other: '',
        id: 'alerts',
        parameters: {
            days: 14,
            limit: 1000,
            output_cols: ['severity', 'name', 'src_ip', 'src_port', 'dest_ip', 'dest_port', 'signature', 'signature_id'],
            severity: { 1: true, 2: true, 3: false, 4: false },
            sort_cols: [['severity', 'asc'], ['name', 'desc']],

        },
        recipients: [user.username],
        title: '',
    });
    const [recipients, setRecipients] = useState([]);
    const [reportColumns, setReportColumns] = useState(_cloneDeep(columns));
    const [ogReportColumns, setOGReportColumns] = useState(_cloneDeep(columns));
    const [, setReportColumnsChange] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
    const [reportNames, setReportNames] = useState([]);
    const reportForm = useValidate();
    const userPerms = Auth.permissions.get('Users');
    const [maxAge, setMaxAge] = useState(0);
    const { systemSettings } = useContext(DataContext);

    const FORMATS = [
        { name: 'CSV', value: 'csv' },
        { name: 'Plain Text', value: 'rst' },
        { name: 'HTML', value: 'html' }
    ];
    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    useEffect(() => {
        if (systemSettings?.reports) {
            if (Object.prototype.hasOwnProperty.call(systemSettings.reports, 'max_age')) {
                setMaxAge(systemSettings.reports.max_age);
            }
        }
    }, [systemSettings]);

    useEffect(() => {
        ReportsService.getReports().then(res => {
            setReportNames(res.data.map(v => v.data.title));
        });
        ReportsService.getReportConstants().then((res) => {
            setReportConstants(res);
            if (location.search) {
                setIsUpdate(true);
                const r = JSON.parse(atob(location.search.replace('?r=', '')));
                setReportUUID(r.uuid);
                // pdf is forbidden
                if (r.data.attachments) {
                    delete r.data.attachments.pdf;
                    if (!Object.values(r.data.attachments).some(v => v)) {
                        r.data.attachments.csv = true;
                    }
                }
                setReport(r.data);
                if (r.data.parameters) {
                    let rc = [...reportColumns];
                    if (r.data.parameters.output_cols) {
                        rc = rc.map(v => {
                            if (r.data.parameters.output_cols.indexOf(v.value) !== -1) {
                                return { ...v, selected: true };
                            }
                            return { ...v, selected: false };
                        });
                    }
                    if (r.data.parameters.sort_cols) {
                        rc = rc.map(v => {
                            const i = r.data.parameters.sort_cols.findIndex(x => x[0] === v.value);
                            if (i !== -1) {
                                return { ...v, sort: r.data.parameters.sort_cols[i][1] };
                            }
                            return { ...v, sort: '' };
                        });
                    }
                    setReportColumns(rc);
                    setOGReportColumns(_cloneDeep(rc));
                }
            }
        });
        if (userPerms.read) {
            UsersService.getUsers().then((res) => {
                setRecipients(res.data);
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setReportColumnsChange(!_isEqual(reportColumns, ogReportColumns));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportColumns]);

    const handleUpdateReportData = (e, validID, valid) => {
        if (validID) {
            reportForm.set({ ...reportForm.get(), [validID]: valid });
        }

        setReport({
            ...report,
            [e.target.name]: e.target.value,
        });
    };

    const handleUpdateReportFrequency = (e) => {
        setReport({
            ...report,
            frequency: e.target.value,
        });
    };

    const handleUpdateReportRecipients = (e) => {
        setReport({
            ...report,
            recipients: e.target.value,
        });
    };

    const handleUpdateOptions = (e) => {
        setReport({
            ...report,
            [e.currentTarget.name]: e.currentTarget.checked
        });
    };

    const handleUpdateOtherFrequency = (e) => {
        setReport({
            ...report,
            additional_recipients: e.currentTarget.value ? e.currentTarget.value.replace(/\s/g, '').split(',') : [],
        });
    };

    // const handleUpdateUserProfile = (e) => {
    //     let u = {...user};
    //     u.data.profile[e.target.name] = e.target.value;
    //     setUser(u);
    // };

    const handleSeverityUpdate = (e) => {
        const r = { ...report };
        r.parameters.severity[e.currentTarget.name] = e.currentTarget.checked;
        setReport(r);
    };

    const handleUpdateReportDecls = (e, component) => {
        const r = { ...report };

        r.id = component.props.value;
        if (report.id === 'sensors' && component.props.value === 'alerts') {
            r.parameters = _cloneDeep(DEFAULT_PARAMETERS);
        }

        setReport(r);
    };

    const handleUpdateParameters = (e) => {
        const r = { ...report };
        r.parameters[e.target.name] = parseInt(e.target.value);
        setReport(r);
    };

    const handleUpdateFormat = (e) => {
        const r = { ...report };
        r.attachments[e.currentTarget.name] = e.currentTarget.checked;
        if (e.currentTarget.name === 'csv' && !r.attachments[e.currentTarget.name]) {
            r.csv_header = false;
        }
        setReport(r);
    };

    const handleDeselectRecipient = (name) => {
        const r = { ...report };
        const i = report.recipients.indexOf(name);
        if (i !== -1) {
            r.recipients.splice(i, 1);
            setReport(r);
        }
    };

    const handleCreateReport = () => {
        const r = _cloneDeep(report);
        if (r.id === 'sensors') {
            r.parameters = {};
        } else {
            r.parameters.output_cols = reportColumns.map(v => {
                return v.selected ? v.value : null;
            }).filter(v => v != null);
            r.parameters.sort_cols = reportColumns.map(v => {
                return v.sort ? [v.value, v.sort] : null;
            }).filter(v => v != null);
        }
        if (isUpdate) {
            ReportsService.updateReport(reportUUID, r).then(() => {
                toast.success('Report template updated successfully');
                navigate('/system/reports');
            });
        } else {
            ReportsService.createReport(r).then(() => {
                toast.success('Report template created successfully');
                navigate('/system/reports');
            });
        }
    };

    const handleCancel = () => {
        setReportColumns(_cloneDeep(columns));
        navigate('/system/reports');
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragCancel = () => {
        setActiveId(null);
    };

    const handleDragEnd = (e) => {
        const { active, over } = e;

        if (active.id !== over.id) {
            setReportColumns((reportColumns) => {
                const oldIndex = reportColumns.findIndex(v => v.name === active.id);
                const newIndex = reportColumns.findIndex(v => v.name === over.id);

                return arrayMove(reportColumns, oldIndex, newIndex);
            });
        }

        setActiveId(null);
    };

    const handleRemoveColumn = (id) => {
        const r = [...reportColumns];
        const i = r.findIndex(v => v.name === id);
        r[i].selected = false;
        r[i].sort = '';
        setReportColumns(r);
    };

    const handleAddColumn = (id) => {
        const r = [...reportColumns];
        const i = r.findIndex(v => v.name === id);
        r[i].selected = true;
        setReportColumns(r);
    };

    const handleSortAsc = (name) => {
        const r = [...reportColumns];
        const i = r.findIndex(v => v.name === name);
        if (r[i].sort === 'asc') {
            r[i].sort = '';
        } else if (r[i].sort === 'desc') {
            r[i].sort = 'asc';
        } else {
            if (reportColumns.filter(v => v.sort).length < 3) {
                r[i].sort = 'asc';
            }
        }
        setReportColumns(r);
    };

    const handleSortDesc = (name) => {
        const r = [...reportColumns];
        const i = r.findIndex(v => v.name === name);
        if (r[i].sort === 'desc') {
            r[i].sort = '';
        } else if (r[i].sort === 'asc') {
            r[i].sort = 'desc';
        } else {
            if (reportColumns.filter(v => v.sort).length < 3) {
                r[i].sort = 'desc';
            }
        }
        setReportColumns(r);
    };

    useEffect(() => {
        const r = { ...report };
        const output = reportColumns.filter(v => v.selected
        ).map(x => x.value);
        const sort = reportColumns.filter(v => v.sort
        ).map(y => [y.value, y.sort]);

        r.parameters.output_cols = output;
        r.parameters.sort_cols = sort;
        setReport(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportColumns]);

    return (
        <Grid container item spacing={2} direction="row" justifyContent="center" alignItems="center" xs={12}>
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Grid container spacing={5} direction="row" justifyContent="center">
                            <Grid container item xs={12} spacing={2}>
                                <Grid item xs={6}>
                                    <Box mt={1}>
                                        <Validator
                                            errorText="Title cannot be empty"
                                            label="Title"
                                            aria-label="title"
                                            margin="normal"
                                            fullWidth
                                            value={report.title}
                                            name="title"
                                            onChange={handleUpdateReportData}
                                        />
                                        <Box display={reportNames.indexOf(report.title) !== -1 && !isUpdate ? 'block' : 'none'}>
                                            <FormHelperText error>This name is already taken</FormHelperText>
                                        </Box>
                                    </Box>
                                    <Box mt={2}>
                                        <FormLabel>Schedule</FormLabel>
                                        <FormGroup row>
                                            <Box>
                                                <RadioGroup row value={report.frequency} name="frequency" onChange={handleUpdateReportFrequency}>
                                                    {reportConstants.report_freqs && Object.keys(reportConstants.report_freqs).map((v, i) => {
                                                        return (<FormControlLabel
                                                            key={'report-frequencies-' + i}
                                                            value={v}
                                                            labelPlacement="start"
                                                            label={reportConstants.report_freqs[v]}
                                                            aria-label={reportConstants.report_freqs[v]}
                                                            control={<Radio inputProps={{ name: 'frequency' }} />}
                                                        />);
                                                    })}
                                                </RadioGroup>
                                            </Box>
                                        </FormGroup>
                                    </Box>
                                    <Box mt={1} display={report.frequency === '@every' ? 'block' : 'none'}>
                                        <TextField
                                            label={<span>Enter an interval in the form <code>0h 0m 0s</code></span>}
                                            aria-label="interval"
                                            margin="normal"
                                            fullWidth
                                            value={report.frequency_other}
                                            name="frequency_other"
                                            onChange={handleUpdateReportData}
                                        />
                                    </Box>
                                    <Box mt={1}>
                                        <FormLabel>Attachments</FormLabel>
                                        <FormGroup row>
                                            {FORMATS.map((v, i) => {
                                                return <FormControlLabel
                                                    key={'report-format-checkbox-' + i}
                                                    labelPlacement="start"
                                                    control={
                                                        <Checkbox
                                                            checked={report.attachments[v.value]}
                                                            onChange={handleUpdateFormat}
                                                            name={v.value}
                                                        />
                                                    }
                                                    label={v.name} />;
                                            })}
                                        </FormGroup>
                                    </Box>
                                    <Box mt={1}>
                                        <FormLabel>Options</FormLabel>
                                        <FormGroup>
                                            <Box>
                                                <FormControlLabel
                                                    labelPlacement="start"
                                                    control={
                                                        <Checkbox
                                                            checked={report.body_text}
                                                            name="body_text"
                                                            onChange={handleUpdateOptions}
                                                        />
                                                    }
                                                    label="Send report text as email body"
                                                />
                                            </Box>
                                            <Box>
                                                <FormControlLabel
                                                    disabled={!report.attachments.csv}
                                                    labelPlacement="start"
                                                    control={
                                                        <Checkbox
                                                            checked={report.csv_header}
                                                            name="csv_header"
                                                            onChange={handleUpdateOptions}
                                                        />
                                                    }
                                                    label="Include CSV header row"
                                                />
                                            </Box>
                                        </FormGroup>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box mt={1}>
                                        <FormControl fullWidth>
                                            <FormLabel>Report Type</FormLabel>
                                            <Select value={report.id} onChange={handleUpdateReportDecls} name="id">
                                                {reportConstants.report_decls && Object.keys(reportConstants.report_decls).map((v, i) => {
                                                    return <MenuItem
                                                        key={'report-decls-' + i}
                                                        value={reportConstants.report_decls[v].id}
                                                    >
                                                        {reportConstants.report_decls[v].title}
                                                    </MenuItem>;
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    {userPerms.read && <Box mt={1}>
                                        <FormControl className={classes.select}>
                                            <InputLabel>Recipients</InputLabel>
                                            <Select
                                                labelId="recipients-select-label"
                                                aria-label="select recipients"
                                                value={report.recipients}
                                                multiple
                                                onChange={handleUpdateReportRecipients}
                                                input={<Input id="recipients-select-input" />}
                                                renderValue={(recipientsSelected) => (
                                                    <div className={classes.chips}>
                                                        {recipientsSelected.map((v, i) => {
                                                            return <Chip
                                                                key={'recipient-chip-' + i}
                                                                label={v}
                                                                className={classes.chip}
                                                                onMouseDown={(e) => { e.stopPropagation() }}
                                                                onDelete={() => { handleDeselectRecipient(v) }}
                                                            />;
                                                        })}
                                                    </div>
                                                )}
                                                MenuProps={MenuProps}
                                            >
                                                {recipients.map((v, i) => <MenuItem key={'recipient-select-' + i} value={v.username}>{v.username} - {v.name}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Box>}
                                    <Box mt={1}>
                                        <TextField
                                            label="Additional Email Recipients"
                                            aria-label="additional email recipients"
                                            helperText="Comma separated emails"
                                            margin="normal"
                                            fullWidth
                                            value={report.additional_recipients}
                                            onChange={handleUpdateOtherFrequency}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid container item xs={12} spacing={2} style={{ display: report.id === 'alerts' ? 'flex' : 'none' }}>
                                <Grid item xs={12}>
                                    <FormLabel>Parameters</FormLabel>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box>
                                        <Validator
                                            valueType="range"
                                            errorText={`Date must be within 1 and ${maxAge ? maxAge : '365'}`}
                                            type="number"
                                            label="Date Range"
                                            helperText={`Previous days to query: 1-${maxAge ? maxAge : '365'}`}
                                            margin="normal"
                                            fullWidth
                                            value={report.parameters.days}
                                            name="days"
                                            inputProps={{
                                                min: 1,
                                                max: maxAge ? maxAge : 365,
                                            }}
                                            onChange={handleUpdateParameters}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    {report.id === 'alerts' && <Box>
                                        <FormLabel>Severity Filter</FormLabel>
                                        <FormGroup row>
                                            {['1', '2', '3', '4'].map((v) => {
                                                return <FormControlLabel
                                                    key={'severity-checkbox-' + v}
                                                    labelPlacement="end"
                                                    label={v}
                                                    control={
                                                        <Checkbox
                                                            checked={_get(report, `parameters.severity[${v}]`, false)}
                                                            onChange={handleSeverityUpdate}
                                                            name={v}
                                                        />
                                                    }
                                                />;
                                            })}
                                        </FormGroup>
                                        <FormHelperText>Please select at least one severity.</FormHelperText>
                                    </Box>}
                                </Grid>
                                <Grid item xs={4}>
                                    <Box>
                                        <Validator
                                            valueType="range"
                                            errorText="Record limit must be with 1 and 5000"
                                            type="number"
                                            label="Record limit"
                                            helperText="Query record limit: 1-5000"
                                            margin="normal"
                                            fullWidth
                                            value={report.parameters.limit}
                                            name="limit"
                                            inputProps={{
                                                min: 1,
                                                max: 5000,
                                            }}
                                            onChange={handleUpdateParameters}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} style={{ display: report.id === 'alerts' ? 'flex' : 'none' }}>
                                <Box>
                                    <Box display="flex" width="100%" flexWrap="wrap">
                                        <Typography>Selected Columns</Typography>
                                        <Box ml={2}><FormHelperText>You can sort up to three columns</FormHelperText></Box>
                                        <Box display="flex" width="100%" flexWrap="wrap">
                                            <DndContext
                                                sensors={sensors}
                                                collisionDetection={closestCenter}
                                                onDragStart={handleDragStart}
                                                onDragEnd={handleDragEnd}
                                                onDragCancel={handleDragCancel}
                                            >
                                                <SortableContext
                                                    items={reportColumns}
                                                    strategy={rectSortingStrategy}
                                                >
                                                    {reportColumns.map(v => v.selected ? <SortableColumn
                                                        key={v.name}
                                                        id={v.name}
                                                        name={v.name}
                                                        sort={v.sort}
                                                        onSortAsc={() => { handleSortAsc(v.name) }}
                                                        onSortDesc={() => { handleSortDesc(v.name) }}
                                                        onRemove={() => { handleRemoveColumn(v.name) }}
                                                    /> : null)}
                                                </SortableContext>
                                                <DragOverlay>
                                                    {activeId ? <Column id={activeId} name={activeId} onRemove={handleRemoveColumn} /> : null}
                                                </DragOverlay>
                                            </DndContext>
                                        </Box>
                                    </Box>
                                    <Box mt={2} display="flex" width="100%" flexWrap="wrap">
                                        <Typography>Available Columns</Typography>
                                        <Box display="flex" width="100%" flexWrap="wrap">
                                            {reportColumns.map(v => !v.selected ? <Chip
                                                style={{ margin: '5px 5px' }}
                                                clickable
                                                color="primary"
                                                key={v.name}
                                                label={v.name}
                                                onClick={() => { handleAddColumn(v.name) }}
                                            /> : null)}
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid container spacing={5} direction="row" justifyContent="center">
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Box ml={2}><Button variant="contained" color="secondary" aria-label="Cancel" onClick={handleCancel}>Cancel</Button></Box>
                                    <Box ml={2}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            aria-label={isUpdate ? 'Update' : 'Create'}
                                            onClick={handleCreateReport}
                                            disabled={!(checkValidity(reportForm.get()) && !(reportNames.indexOf(report.title) !== -1 && !isUpdate))}
                                        >{isUpdate ? 'Update' : 'Create'}</Button></Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

CreateReport.propTypes = {};

export default CreateReport;
